import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { format } from 'date-fns';
import { get } from 'lodash';
import { catchError, map, mergeMap, of, tap, withLatestFrom } from 'rxjs';
import { EventService } from '@providers/event/event.service';
import { DailyActivityAPI } from './activity';
import { dailyActivityActions } from './activity.action';
import {
  DailyActivity,
  DailyActivityCategory,
  UserDailyActivity
} from './activity.interface';
import { selectSelectedCategory } from './activity.selectors';

/**
 * Daily Activity Effect.
 */
@Injectable()
export class DailyActivityEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(DailyActivityAPI);
  private readonly event = inject(EventService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);

  deleteUserActivity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dailyActivityActions.deleteUserActivity),
      mergeMap(({ id, isFromDR, payload }) =>
        this.api.deleteUserDailyActivity(id).pipe(
          map(({ message }) => {
            if (isFromDR && payload) {
              return dailyActivityActions.saveUserActivity({
                payload,
                isFromDR: true
              });
            }
            return dailyActivityActions.deleteUserActivitySuccess({
              message
            });
          }),
          catchError((error) =>
            of(dailyActivityActions.deleteUserActivityFailure({ error }))
          )
        )
      )
    )
  );

  deleteUserActivityFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(dailyActivityActions.deleteUserActivityFailure),
        tap(({ error }) => {
          this.event.publish(
            this.event.keys.TOAST_ERROR,
            get(
              error,
              'error.message',
              'An error occurred while deleting the activity'
            )
          );
        })
      ),
    { dispatch: false }
  );

  deleteUserActivitySuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(dailyActivityActions.deleteUserActivitySuccess),
        tap(async ({ message }) => {
          await this.router.navigate(['/tabs/home']);
          this.event.publish(this.event.keys.TOAST_SUCCESS, message);
        })
      ),
    { dispatch: false }
  );

  fetchActivities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dailyActivityActions.fetchActivities),
      withLatestFrom(this.store.select(selectSelectedCategory)),
      mergeMap(([, selectedCategory]) =>
        this.api
          .getDailyActivities({
            isActive: true,
            limit: 100,
            dailyActivityCategoryId: selectedCategory
              ? selectedCategory.id
              : undefined,
            includeSegmentsAndItemsDetail: true
          })
          .pipe(
            mergeMap((res) => {
              const activities = res.data?.results as Array<DailyActivity>;
              const fitnessActivity = activities.find(
                (a) => a.code === 'FITNESS'
              );

              return [
                dailyActivityActions.fetchActivitiesSuccess({ activities }),
                ...(fitnessActivity
                  ? [
                      dailyActivityActions.setFitnessActivity({
                        fitnessActivity
                      })
                    ]
                  : [])
              ];
            }),
            catchError((error) =>
              of(
                dailyActivityActions.fetchActivitiesFailure({
                  error
                })
              )
            )
          )
      )
    )
  );

  fetchActivitiesFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(dailyActivityActions.fetchActivitiesFailure),
        tap(({ error }) => {
          this.event.publish(
            this.event.keys.TOAST_ERROR,
            get(error, 'error.message', 'Failed to load activities')
          );
        })
      ),
    { dispatch: false }
  );

  fetchCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dailyActivityActions.fetchCategories),
      mergeMap(() =>
        this.api
          .getDailyActivityCategories({
            isActive: true
          })
          .pipe(
            map((res) => {
              const categories = res.data
                ?.results as Array<DailyActivityCategory>;
              return dailyActivityActions.fetchCategoriesSuccess({
                categories
              });
            }),
            catchError((error) =>
              of(
                dailyActivityActions.fetchCategoriesFailure({
                  error
                })
              )
            )
          )
      )
    )
  );

  fetchCategoriesFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(dailyActivityActions.fetchCategoriesFailure),
        tap(({ error }) => {
          this.event.publish(
            this.event.keys.TOAST_ERROR,
            get(error, 'error.message', 'Failed to load categories')
          );
        })
      ),
    { dispatch: false }
  );

  fetchUserActivities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dailyActivityActions.fetchUserActivities),
      mergeMap(() => {
        const today = new Date();
        const formatted = format(today, 'yyyy-MM-dd');
        return this.api
          .getUserDailyActivities({
            limit: 100,
            startDate: formatted,
            endDate: formatted
          })
          .pipe(
            map((res) => {
              const userActivities = res.data
                ?.results as Array<UserDailyActivity>;
              return dailyActivityActions.fetchUserActivitiesSuccess({
                userActivities
              });
            }),
            catchError((error) =>
              of(dailyActivityActions.fetchUserActivitiesFailure({ error }))
            )
          );
      })
    )
  );

  fetchUserActivitiesFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(dailyActivityActions.fetchUserActivitiesFailure),
        tap(({ error }) => {
          this.event.publish(
            this.event.keys.TOAST_ERROR,
            get(error, 'error.message', 'Failed to load user activities')
          );
        })
      ),
    { dispatch: false }
  );

  saveUserActivity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dailyActivityActions.saveUserActivity),
      mergeMap(({ payload, isFromDR }) =>
        this.api.createUserDailyActivity(payload).pipe(
          map(({ message }) =>
            dailyActivityActions.saveUserActivitySuccess({
              message
            })
          ),
          catchError((error) => {
            if (isFromDR && error.error.data.id) {
              return of(
                dailyActivityActions.deleteUserActivity({
                  id: error.error.data.id,
                  isFromDR: true,
                  payload
                })
              );
            }
            return of(dailyActivityActions.saveUserActivityFailure({ error }));
          })
        )
      )
    )
  );

  saveUserActivityFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(dailyActivityActions.saveUserActivityFailure),
        tap(({ error }) => {
          this.event.publish(
            this.event.keys.TOAST_ERROR,
            get(
              error,
              'error.message',
              'An error occurred while saving the activity'
            )
          );
        })
      ),
    { dispatch: false }
  );

  saveUserActivitySuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(dailyActivityActions.saveUserActivitySuccess),
        tap(async () => {
          await this.router.navigate(['/tabs/home']);
        })
      ),
    { dispatch: false }
  );

  updateUserActivity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dailyActivityActions.updateUserActivity),
      mergeMap(({ id, payload }) =>
        this.api.updateUserDailyActivity(id, payload).pipe(
          map(({ message }) =>
            dailyActivityActions.updateUserActivitySuccess({
              message
            })
          ),
          catchError((error) =>
            of(dailyActivityActions.saveUserActivityFailure({ error }))
          )
        )
      )
    )
  );

  updateUserActivityFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(dailyActivityActions.updateUserActivityFailure),
        tap(({ error }) => {
          this.event.publish(
            this.event.keys.TOAST_ERROR,
            get(
              error,
              'error.message',
              'An error occurred while saving the activity'
            )
          );
        })
      ),
    { dispatch: false }
  );

  updateUserActivitySuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(dailyActivityActions.updateUserActivitySuccess),
        tap(() => {
          this.router.navigate(['/tabs/home']);
        })
      ),
    { dispatch: false }
  );
}
